import * as React from 'react';
import './ClientForm.scss';
import { TK } from '../../../store/translations/translationKeys';
import { Spinner, Form, Modal, ModalHeader, ModalBody, ModalFooter, InputGroup, InputGroupAddon } from 'reactstrap';
import { useTranslations } from '../../../store/translations/hooks';
import { TextInputField, SexField, AutocompleteTextField, NumberInputField, MultiplePhoneNumberField, DateField } from '../fields';
import { ClientStatus, FeatureEnum, PermissionEnum, SexEnum } from '../../../models';
import Warning from '@material-ui/icons/Warning';
import Delete from '@material-ui/icons/Delete';
import LaunchIcon from '@material-ui/icons/Launch';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import SecurityIcon from '@material-ui/icons/Security';
import { ClientData } from '../../../models';
import ChangeLogInfo from '../../ChangeLogInfo';
import { ChangeLog } from '../../../models';
import { getAge } from '../../../shared/utils';
import { useCallback, useMemo } from 'react';
import Typography from '../../Typography';
import { getCCData } from '../../../ajax/requests';
import { useSelector, useDispatch } from 'react-redux';
import { getFeatures, getMyProffesionalData, getPermissions } from '../../../store/session/selectors';
import { CheckInputField } from '../fields/CheckInputField';
import { useGeneralSettings } from '../../../store/settings/hooks';
import { ComboField } from '../fields/ComboField';
import { ClientTags } from '../fields/ClientTags';
import { getEntitiesDataItems } from '../../../store/entities/selectors';
import { ClientEntity, ProfessionalEntity } from '../../../store/entities/state';
import { useBreakpoints } from '../../../shared/hooks/useBreakpoints';
import ClientConsentBadges from '../../ClientConsentBadges';
import {
    createClientSearchConfig,
} from '../../../shared/clientSearch';
import { ApplicationState } from '../../../store';
import { openClient } from '../../../store/entities/actions';
import AppButton from '../../AppButton';

const isClientAccessibleByProfessional = (
    data: Pick<ClientData, 'professionalId' | 'additionalProfessionalIds' | 'visibleForAllProfessionals'>,
    professionalId?: string,
) => {
    if (data.visibleForAllProfessionals) {
        return true;
    }

    if (!professionalId) {
        return false;
    }

    return data.professionalId === professionalId
        || (data.additionalProfessionalIds?.includes(professionalId) ?? false);
};

const normalizeAdditionalProfessionalIds = (
    professionalId?: string,
    additionalProfessionalIds?: string[],
) => (additionalProfessionalIds ?? [])
    .map(id => id?.trim())
    .filter((id): id is string => !!id && id !== professionalId)
    .filter((id, index, items) => items.indexOf(id) === index);

export interface ClientFormStateProps {
    data: ClientData,
    hasChanged: boolean,
    isSubmitting?: boolean,
    type: 'edit' | 'create',
    canManageClients: boolean,
    changeLog?: ChangeLog[],
    hideActions?: boolean,
}

export interface ClientFormDispatchProps {
    onChange: (data: ClientData) => void,
    onLoad?: () => void,
    onSave: (data: ClientData) => void,
    onCancel?: () => void,
    onDelete?: (migrateToClientId?: string) => void,
    onResetClientPassword?: (data: ClientData) => void,
    onOpenClient?: () => void,
}

export type ClientFormProps = ClientFormStateProps & ClientFormDispatchProps;

const ClientForm: React.FC<ClientFormProps> = ({
    type,
    data,
    hasChanged,
    isSubmitting,
    onChange,
    onLoad,
    onSave,
    onCancel,
    onDelete,
    onResetClientPassword,
    onOpenClient,
    canManageClients,
    changeLog,
    hideActions,
}) => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const generalSettings = useGeneralSettings();
    const {clientOrigins, clientSubsystems} = generalSettings;
    const fixedClientProperties = generalSettings.fixedClientProperties ?? {};
    const isFieldHidden = (id: string) => fixedClientProperties[id]?.hidden === true;
    const features = useSelector(getFeatures);
    const permissions = useSelector(getPermissions);
    const myProfessional = useSelector(getMyProffesionalData);
    const breakpoints = useBreakpoints();
    const consentSettings = useSelector((state: ApplicationState) => state.settings.consentManagement);
    const professionals = useSelector(getEntitiesDataItems<ProfessionalEntity>('professionals'));
    const professionalCanEditOwnClient = useMemo(() => {
        if (type === 'create') return true;
        if (!myProfessional) return false;
        if (!isClientAccessibleByProfessional(data, myProfessional.id)) return false;
        return permissions.includes(PermissionEnum.MyClientsManagement);
    }, [data.additionalProfessionalIds, data.professionalId, data.visibleForAllProfessionals, myProfessional, permissions, type]);

    const canEdit = canManageClients || professionalCanEditOwnClient;
    const selectedAdditionalProfessionalIds = useMemo(
        () => normalizeAdditionalProfessionalIds(data.professionalId, data.additionalProfessionalIds),
        [data.additionalProfessionalIds, data.professionalId],
    );
    const allSelectedProfessionalNames = useMemo(() => {
        const result: string[] = [];
        const primaryName = professionals.find(p => p.id === data.professionalId)?.name;
        if (primaryName) result.push(primaryName);
        selectedAdditionalProfessionalIds.forEach(id => {
            const name = professionals.find(p => p.id === id)?.name;
            if (name) result.push(name);
        });
        return result;
    }, [data.professionalId, selectedAdditionalProfessionalIds, professionals]);

    const clients = useSelector(getEntitiesDataItems<ClientEntity>('clients'));
    const migrationClients = useMemo(() => clients.filter(c => c.id !== data.id), [clients, data.id]);
    const migrationClientSearchConfig = useMemo(() => createClientSearchConfig(migrationClients), [migrationClients]);

    const [consentsExpanded, setConsentsExpanded] = React.useState(false);
    const consentSummary = useMemo(() => {
        if (data.consentResult?.approvedDate) {
            const enumCount = Object.values(data.consentResult.approvedConsents || {}).filter(Boolean).length;
            const customCount = data.consentResult.approvedCustomCheckboxes?.length ?? 0;
            const count = enumCount + customCount;
            return {
                text: `${count} ${count === 1 ? t(TK.consentActiveSingular) : t(TK.consentActivePlural)}`,
                tone: 'approved' as const,
            };
        }

        if (data.consentEmailSentAt || data.consentSmsSentAt || data.consentWhatsAppSentAt) {
            return {
                text: t(TK.consentPending),
                tone: 'pending' as const,
            };
        }

        return {
            text: t(TK.consentsNotYetSubmitted),
            tone: 'unsubmitted' as const,
        };
    }, [data.consentResult, data.consentEmailSentAt, data.consentSmsSentAt, data.consentWhatsAppSentAt, t]);

    const [readingIdentityCard, setReadingIdentityCard] = React.useState(false);
    const [downloadCompanion, setDownloadCompanion] = React.useState(false);
    const [canResetClientPassword, setCanResetClientPassword] = React.useState<boolean>(!!(canEdit && onResetClientPassword && data.email))
    const [deleteModal, setDeleteModal] = React.useState(false);
    const [migrateToClientOption, setMigrateToClientOption] = React.useState<string | undefined>(undefined);

    // eslint-disable-next-line
    React.useEffect(() => { 
        if(onLoad) onLoad();
        if (type === 'create' && !!myProfessional && !permissions.includes(PermissionEnum.ClientsManagement)) {
            onChange({
                ...data,
                professionalId: myProfessional.id,
                additionalProfessionalIds: undefined,
                visibleForAllProfessionals: false,
            });
        }
     }, []);

    const isEditing = type === 'edit';

    const handleReadIdentityCard = () => {
        if(readingIdentityCard) return;
        setReadingIdentityCard(true);
        getCCData()
            .then(newData => {
                onChange({...data, ...newData });
                setReadingIdentityCard(false);
            })
            .catch(error => {
                console.log(error)
                setDownloadCompanion(true);
                setReadingIdentityCard(false);
            })
    }

    const handleResetClientPassoword = useCallback(() => {
        if (!canResetClientPassword) return
        setCanResetClientPassword(false)
        onResetClientPassword && onResetClientPassword(data)
    }, [onResetClientPassword, setCanResetClientPassword, canResetClientPassword, data])

    const birthdayLabel = useMemo(() => data?.birthday
        ? `${t(TK.birthdate)} (${getAge(data.birthday)} ${t(TK.yearsOld)})`
        : t(TK.birthdate), [t, data.birthday])

    const statusTranslations: Record<ClientStatus, string> = {
        [ClientStatus.None]: t(TK.unspecified),
        [ClientStatus.Active]: t(TK.active),
        [ClientStatus.Inactive]: t(TK.inactive),
    }

    const statusFromTranslation = (text?: string): ClientStatus => {
        switch(text) {
            case t(TK.active): return ClientStatus.Active
            case t(TK.inactive): return ClientStatus.Inactive
            default: return ClientStatus.None
        }
    }

    const handleSave = () => {
        onSave({
            ...data,
            identificationNumber: data.identificationNumber?.trim(),
            name: data.name?.trim(),
            taxNumber: data.taxNumber?.trim(),
            healthNumber: data.healthNumber?.trim(),
            subsystemName: data.subsystemName?.trim(),
            subsystemNumber: data.subsystemNumber?.trim(),
            job: data.job?.trim(),
            origin: data.origin?.trim(),
            addressLine1: data.addressLine1?.trim(),
            addressLine2: data.addressLine2?.trim(),
            zipCode: data.zipCode?.trim(),
            city: data.city?.trim(),
            phoneNumbers: data.phoneNumbers?.map(p => p.trim()),
            email: data.email?.trim(),
        })
    }

    return (
        <Form style={{display: 'flex', marginTop: 'var(--spacer-S)', flexDirection: 'column', gap: 'var(--spacer-M)'}}>
            <div style={{display: 'flex', gap: 'var(--spacer-S)'}}>
                <div style={{flexGrow: 1}}>
                    <div style={{display: 'flex', alignItems: 'center'}}>
                        <div style={{flexGrow: 1}}>
                            <TextInputField
                                id={'client-name'}
                                label={t(TK.name)}
                                value={data.name || ''}
                                disabled={!canEdit}
                                onChange={name => onChange({...data, name})}
                            />
                        </div>
                        {isEditing && onOpenClient && (
                            <AppButton className="entityLinkButton" color="link" onClick={onOpenClient}>
                                <LaunchIcon/>
                            </AppButton>
                        )}
                    </div>
                    <div style={{display: 'flex', flexDirection: 'column'}}>
                        <div style={{display: 'flex', gap: 'var(--spacer-S)', alignItems: 'flex-start', flexWrap: 'wrap', rowGap: 'var(--spacer-XXS)', width: '100%'}}>
                            <div style={{flex: '1 1 50%', minWidth: breakpoints.md ? '100%' : '250px'}}>
                                <ClientTags values={data.tags ?? []} onChange={tags => onChange({...data, tags})} />
                            </div>

                            {features.includes(FeatureEnum.ConsentManagement) && isEditing && (
                                <div style={{width: '100%', display: 'flex', justifyContent: 'flex-start'}}>
                                    <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start'}}>
                                    <div
                                        onClick={() => setConsentsExpanded(e => !e)}
                                        style={{display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 'var(--spacer-XS)', cursor: 'pointer', userSelect: 'none', marginBottom: consentsExpanded ? 'var(--spacer-XXS)' : 0, width: '100%'}}
                                    >
                                        <span style={{color: 'var(--text-muted)', fontSize: '0.85em', fontStyle: 'italic', flexShrink: 0}}>
                                            {t(TK.consents)}
                                        </span>
                                        {consentSummary && (
                                            <span
                                                style={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    gap: 'var(--spacer-XXS)',
                                                    padding: 'var(--spacer-XXS) var(--spacer-XS)',
                                                    borderRadius: '12px',
                                                    fontSize: '0.75em',
                                                    color: consentSummary.tone === 'approved' ? 'var(--success-color)' : 'var(--text-muted)',
                                                    backgroundColor: consentSummary.tone === 'approved' ? 'var(--success-bg)' : 'transparent',
                                                    border: consentSummary.tone === 'approved' ? 'var(--border-thin) solid var(--success-border)' : 'none',
                                                    opacity: consentSummary.tone === 'unsubmitted' ? 0.75 : 1,
                                                }}
                                            >
                                                <SecurityIcon style={{fontSize: '0.95em'}} />
                                                <span>{consentSummary.text}</span>
                                            </span>
                                        )}
                                        {consentsExpanded
                                            ? <ExpandLessIcon style={{fontSize: '1.1em', color: 'var(--text-muted)'}} />
                                            : <ExpandMoreIcon style={{fontSize: '1.1em', color: 'var(--text-muted)'}} />
                                        }
                                    </div>
                                    {consentsExpanded && (
                                        <div style={{width: '100%', display: 'flex', justifyContent: 'flex-start'}}>
                                            <ClientConsentBadges
                                                clientId={data.id}
                                                consentResult={data.consentResult}
                                                consentEmailSentAt={data.consentEmailSentAt}
                                                consentSmsSentAt={data.consentSmsSentAt}
                                                consentWhatsAppSentAt={data.consentWhatsAppSentAt}
                                                clientEmail={data.email}
                                                consentSettings={consentSettings}
                                                onPhysicalConsentUploaded={() => dispatch(openClient(data.id))}
                                            />
                                        </div>
                                    )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                { data.photo &&
                    <div>
                        <img className="photo" alt="" src={`data:image/png;base64,${data.photo}`}/>
                    </div>
                }
            </div>
            <div style={{display: 'flex', flexWrap: 'wrap', gap: 'var(--spacer-S)'}}>
                <div style={{width: breakpoints.md ? '100%' : 'calc(25% - var(--spacer-S) * 3/4)'}}>
                    <ComboField 
                        id={'client-status'}
                        label={t(TK.status)}
                        options={Object.values(statusTranslations)}
                        value={data.status ? statusTranslations[data.status] : t(TK.unspecified)}
                        disabled={!canEdit}
                        onChange={statusText => onChange({
                            ...data, 
                            status: statusFromTranslation(statusText),
                        })} 
                    />
                </div>
                {!isFieldHidden('IdentificationNumber') && (
                    <div style={{width: breakpoints.md ? '100%' : 'calc(25% - var(--spacer-S) * 3/4)'}}>
                        <TextInputField
                            id={'client-identification-number'}
                            label={t(TK.identificationNumber)}
                            value={data.identificationNumber || ''}
                            disabled={!canEdit}
                            onChange={identificationNumber => onChange({...data, identificationNumber})}
                        />
                    </div>
                )}
                {!isFieldHidden('Sex') && (
                    <div style={{width: breakpoints.md ? '100%' : 'calc(25% - var(--spacer-S) * 3/4)'}}>
                        <SexField
                            id={'client-sex'}
                            label={t(TK.sex)}
                            value={data.sex || SexEnum.Unspecified}
                            disabled={!canEdit}
                            onChange={sex => onChange({...data, sex})}
                        />
                    </div>
                )}
                {!isFieldHidden('Height') && (
                    <div style={{width: breakpoints.md ? '100%' : 'calc(25% - var(--spacer-S) * 3/4)'}}>
                        <NumberInputField
                            id={'client-height'}
                            label={t(TK.height)}
                            value={data.height ?? 0}
                            disabled={!canEdit}
                            onChange={height => onChange({...data, height})}
                        />
                    </div>
                )}
            </div>

            {!isFieldHidden('PhoneNumbers') && (
                <MultiplePhoneNumberField
                    id={'client-phone-numbers'}
                    values={data.phoneNumbers || ['']}
                    disabled={!canEdit}
                    onChange={phoneNumbers => onChange({...data, phoneNumbers})}
                />
            )}
            
            {(!isFieldHidden('Email') || !isFieldHidden('Birthday')) && (
                <div style={{display: 'flex', flexWrap: 'wrap', gap: 'var(--spacer-S)'}}>
                    {!isFieldHidden('Email') && (
                        <div style={{width: breakpoints.md ? '100%' : 'calc(50% - var(--spacer-S) * 1/2)'}}>
                            <>
                                <TextInputField
                                    id={'client-email'}
                                    label={t(TK.email)}
                                    type="email"
                                    value={data.email || ''}
                                    disabled={!canEdit}
                                    onChange={email => onChange({...data, email})}
                                />
                                {features.includes(FeatureEnum.ClientsLogin) &&
                                    <span hidden={!canResetClientPassword}>
                                        <AppButton style={{textAlign: 'start'}} size='sm' color='link' onClick={handleResetClientPassoword} disabled={!canResetClientPassword}>
                                            {data.hasPortalAccess ? t(TK.sendClientPortalNewPassword) : t(TK.sendClientPortalInvite)}
                                        </AppButton>
                                    </span>
                                }
                                {false && features.includes(FeatureEnum.CRM) && !features.includes(FeatureEnum.ConsentManagement) &&
                                    <CheckInputField
                                        id={'client-allow-marketing-communication'}
                                        label={t(TK.acceptMarketingCommunication)}
                                        value={data.acceptMarktingCommunication}
                                        onChange={acceptMarktingCommunication => onChange({...data, acceptMarktingCommunication})}/>
                                }
                            </>
                        </div>
                    )}
                    {!isFieldHidden('Birthday') && (
                        <div style={{width: breakpoints.md ? '100%' : 'calc(50% - var(--spacer-S) * 1/2)'}}>
                            <DateField
                                id={'client-birthday'}
                                label={birthdayLabel}
                                value={data.birthday || ''}
                                type="date"
                                disabled={!canEdit}
                                onChange={birthday => onChange({...data, birthday})}
                            />
                        </div>
                    )}
                </div>
            )}

            {(!isFieldHidden('TaxNumber') || !isFieldHidden('HealthNumber')) && (
                <div style={{display: 'flex', flexWrap: 'wrap', gap: 'var(--spacer-S)'}}>
                    {!isFieldHidden('TaxNumber') && (
                        <div style={{width: breakpoints.md ? '100%' : 'calc(50% - var(--spacer-S) * 1/2)'}}>
                            <TextInputField
                                id={'client-tax-number'}
                                label={t(TK.taxNumber)}
                                value={data.taxNumber || ''}
                                disabled={!canEdit}
                                onChange={taxNumber => onChange({...data, taxNumber})}
                            />
                        </div>
                    )}
                    {!isFieldHidden('HealthNumber') && (
                        <div style={{width: breakpoints.md ? '100%' : 'calc(50% - var(--spacer-S) * 1/2)'}}>
                            <TextInputField
                                id={'client-health-number'}
                                label={t(TK.healthNumber)}
                                value={data.healthNumber || ''}
                                disabled={!canEdit}
                                onChange={healthNumber => onChange({...data, healthNumber})}
                            />
                        </div>
                    )}
                </div>
            )}

            {(!isFieldHidden('SubsystemName') || !isFieldHidden('SubsystemNumber')) && (
                <div style={{display: 'flex', gap: 'var(--spacer-S)'}}>
                    {!isFieldHidden('SubsystemName') && (
                        <div style={{width: breakpoints.md ? '100%' : 'calc(50% - var(--spacer-S) * 1/2)'}}>
                            <AutocompleteTextField
                                id={'client-subsystem-name'}
                                label={t(TK.subsystemName)}
                                value={data.subsystemName}
                                disabled={!canEdit}
                                options={clientSubsystems}
                                onValueChanged={subsystemName => onChange({...data, subsystemName})}
                            />
                        </div>
                    )}
                    {!isFieldHidden('SubsystemNumber') && (
                        <div style={{width: breakpoints.md ? '100%' : 'calc(50% - var(--spacer-S) * 1/2)'}}>
                            <TextInputField
                                id={'client-subsystem-number'}
                                label={t(TK.subsystemNumber)}
                                value={data.subsystemNumber || ''}
                                disabled={!canEdit}
                                onChange={subsystemNumber => onChange({...data, subsystemNumber})}
                            />
                        </div>
                    )}
                </div>
            )}

            {(!isFieldHidden('Job') || !isFieldHidden('Origin')) && (
                <div style={{display: 'flex', gap: 'var(--spacer-S)'}}>
                    {!isFieldHidden('Job') && (
                        <div style={{width: breakpoints.md ? '100%' : 'calc(50% - var(--spacer-S) * 1/2)'}}>
                            <TextInputField
                                id={'client-job'}
                                label={t(TK.job)}
                                value={data.job || ''}
                                disabled={!canEdit}
                                onChange={job => onChange({...data, job})}
                            />
                        </div>
                    )}
                    {!isFieldHidden('Origin') && (
                        <div style={{width: breakpoints.md ? '100%' : 'calc(50% - var(--spacer-S) * 1/2)'}}>
                            <AutocompleteTextField
                                id={'client-origin'}
                                label={t(TK.origin)}
                                value={data.origin}
                                disabled={!canEdit}
                                options={clientOrigins}
                                onValueChanged={origin => onChange({...data, origin})}
                            />
                        </div>
                    )}
                </div>
            )}

            {!isFieldHidden('Address') && (
                <>
                    <TextInputField
                        id={'client-address-line1'}
                        name="address-line1"
                        label={`${t(TK.address)} 1`}
                        value={data.addressLine1 || ''}
                        disabled={!canEdit}
                        onChange={addressLine1 => onChange({...data, addressLine1})}/>
                    <TextInputField
                        id={'client-address-line2'}
                        name="address-line2"
                        label={`${t(TK.address)} 2`}
                        value={data.addressLine2 || ''}
                        disabled={!canEdit}
                        onChange={addressLine2 => onChange({...data, addressLine2})}/>
                </>
            )}


            {(!isFieldHidden('ZipCode') || !isFieldHidden('City')) && (
                <div style={{display: 'flex', gap: 'var(--spacer-S)'}}>
                    {!isFieldHidden('ZipCode') && (
                        <div style={{width: breakpoints.md ? '100%' : 'calc(50% - var(--spacer-S) * 1/2)'}}>
                            <TextInputField
                                id={'client-postal-code'}
                                label={t(TK.zipCode)}
                                name="postal-code"
                                value={data.zipCode || ''}
                                disabled={!canEdit}
                                onChange={zipCode => onChange({...data, zipCode})}
                            />
                        </div>
                    )}
                    {!isFieldHidden('City') && (
                        <div style={{width: breakpoints.md ? '100%' : 'calc(50% - var(--spacer-S) * 1/2)'}}>
                            <TextInputField
                                id={'client-street-address'}
                                label={t(TK.city)}
                                name="street-address"
                                value={data.city || ''}
                                disabled={!canEdit}
                                onChange={city => onChange({...data, city})}
                            />
                        </div>
                    )}
                </div>
            )}
            
            <div style={{display: 'flex', flexWrap: 'wrap', gap: 'var(--spacer-S)'}}>
                <div style={{width: breakpoints.md ? '100%' : 'calc(75% - var(--spacer-S) * 1/2)'}}>
                    {!data.visibleForAllProfessionals && (
                        <AutocompleteTextField
                            id={'client-professional'}
                            label={t(TK.professional)}
                            multiSelect={professionals.length > 1 && canManageClients}
                            disabled={!canManageClients}
                            options={professionals.map(p => p.name)}
                            value={professionals.length === 1 || !canManageClients ? (professionals.find(p => p.id === data.professionalId)?.name ?? '') : undefined}
                            values={professionals.length > 1 && canManageClients ? allSelectedProfessionalNames : undefined}
                            onValueChanged={professionalName => {
                                onChange({...data, professionalId: professionals.find(p => p.name === professionalName)?.id});
                            }}
                            onValuesChanged={names => {
                                const ids = names
                                    .map(name => professionals.find(p => p.name === name)?.id)
                                    .filter((id): id is string => !!id);
                                const [primaryId, ...additionalIds] = ids;
                                onChange({
                                    ...data,
                                    professionalId: primaryId,
                                    additionalProfessionalIds: additionalIds.length ? additionalIds : undefined,
                                    visibleForAllProfessionals: false,
                                });
                            }}
                        />
                    )}
                    {(allSelectedProfessionalNames.length > 1 || data.visibleForAllProfessionals) && (
                        <CheckInputField
                            id={'client-visible-for-all-professionals'}
                            label={t(TK.visibleForAllProfessionals)}
                            value={!!data.visibleForAllProfessionals}
                            onChange={visibleForAllProfessionals => onChange({...data, visibleForAllProfessionals})}
                        />
                    )}
                </div>
                <div style={{width: breakpoints.md ? '100%' : 'calc(25% - var(--spacer-S) * 1/2)'}}>
                    <NumberInputField 
                        id={'client-professional-notification-days'}
                        label={t(TK.inactivityNotificationAfter)}
                        disabled={!canEdit}
                        append={t(TK.days).toLocaleLowerCase()}
                        value={data.professionalAutomaticNotificationInDays || 0}
                        onChange={professionalAutomaticNotificationInDays => onChange({...data, professionalAutomaticNotificationInDays})}
                    />
                </div>
            </div>

            {!isFieldHidden('WarningNote') && (
                <TextInputField
                    id={'client-warning-note'}
                    label={<span className="warningNote"><Warning/>{t(TK.warning)}</span>}
                    value={data.warningNote || ''}
                    disabled={!canEdit}
                    onChange={warningNote => onChange({...data, warningNote})}
                />
            )}

            {!isFieldHidden('Notes') && (
                <TextInputField
                    id={'client-notes'}
                    label={t(TK.notes)}
                    type="textarea"
                    value={data.notes || ''}
                    disabled={!canEdit}
                    onChange={notes => onChange({...data, notes})}
                />
            )}

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--spacer-S)' }}>
            {Object.entries(generalSettings.clientProperties ?? {}).map(([name, settings]) => {
                const profCanView = settings.professionalCanView ?? true;
                const profCanEdit = settings.professionalCanEdit ?? true;
                if (myProfessional && !profCanView) return null;
                const fieldDisabled = !canEdit || (!!myProfessional && !profCanEdit);
                const value = data.properties?.[name] ?? '';
                const onPropChange = (val: string) => onChange({
                    ...data,
                    properties: { ...(data.properties ?? {}), [name]: val },
                });
                const helpText = settings.description || undefined;
                const append = settings.unit || undefined;
                const fieldWidth = settings.width === 'medium'
                    ? (breakpoints.md ? '100%' : 'calc(50% - var(--spacer-S) * 1/2)')
                    : settings.width === 'small'
                    ? (breakpoints.md ? '100%' : 'calc(25% - var(--spacer-S) * 3/4)')
                    : '100%';
                const field = settings.type === 'number' ? (
                    <NumberInputField
                        key={name}
                        id={`client-property-${name}`}
                        label={name}
                        helpText={helpText}
                        append={append}
                        value={parseFloat(value) || 0}
                        disabled={fieldDisabled}
                        onChange={v => onPropChange(String(v ?? 0))}
                    />
                ) : (
                    <TextInputField
                        key={name}
                        id={`client-property-${name}`}
                        label={name}
                        helpText={helpText}
                        type={settings.type === 'multiline' ? 'textarea' : 'text'}
                        value={value}
                        disabled={fieldDisabled}
                        onChange={onPropChange}
                    />
                );
                return (
                    <div key={name} style={{ width: fieldWidth }}>
                        {field}
                    </div>
                );
            })}
            </div>

            { !hideActions && <div className="buttonsContainer" >
                { canEdit &&
                    <AppButton
                        onClick={handleReadIdentityCard}
                        disabled={readingIdentityCard}
                    >
                        {readingIdentityCard && <Spinner size="sm" color="secundary"/>}
                        {t(TK.readIdentifyCard)}
                    </AppButton>
                }
                { isEditing && canEdit && onDelete && (
                    <AppButton id={'client-delete-button'} outline color="danger" onClick={() => setDeleteModal(true)}>
                        <Delete />
                    </AppButton>
                )}
                { onCancel && (<AppButton id={'client-cancel-button'} onClick={onCancel}>{t(TK.cancel)}</AppButton>)}
                { canEdit &&
                    <AppButton
                        id={'client-save-button'}
                        color="success"
                        disabled={isSubmitting || !hasChanged}
                        onClick={handleSave}
                    >
                        {isSubmitting && <Spinner size="sm" color="secundary"/>}
                        {t(isEditing ? TK.save : TK.create)}
                    </AppButton>
                }
                { !!changeLog?.length && <ChangeLogInfo changeLog={changeLog}/> }
            </div> }
            
            { downloadCompanion &&
                <Modal size="lg" isOpen={downloadCompanion} toggle={() => setDownloadCompanion(false)}>
                    <ModalHeader toggle={() => setDownloadCompanion(false)}>
                        {t(TK.companionConnectionIssueTitle)}
                    </ModalHeader>
                    <ModalBody>                    
                        <Typography block>{t(TK.unableToReadIdentityCardMessage)}</Typography>
                        <br/>
                        <Typography block>{t(TK.companionConnectionIssueMessage)}</Typography>
                        <br/>
                        <Typography block>{t(TK.companionChromeTroubleshootingTitle)}</Typography>
                        <ol>
                            <li>{t(TK.companionChromeTroubleshootingStep1)}</li>
                            <li>{t(TK.companionChromeTroubleshootingStep2)}</li>
                            <li>{t(TK.companionChromeTroubleshootingStep3)}</li>
                            <li>{t(TK.companionChromeTroubleshootingStep4)}</li>
                        </ol>
                        <Typography block>{t(TK.companionTryEdgeMessage)}</Typography>
                        <br/>
                        <Typography block>{t(TK.companionRequirementsMessage)}</Typography>
                        <br/>
                        <Typography block>{t(TK.downloadCompanionAppMessage)}</Typography>
                    </ModalBody>
                    <ModalFooter>
                        <AppButton href="https://companion.appointmint.pt" target="_blank" color="success">{t(TK.downloadAppointmintCompanion)}</AppButton>
                        <AppButton href="https://www.autenticacao.gov.pt/web/guest/cc-aplicacao" target="_blank" color="success">{t(TK.downloadAutenticacaoGov)}</AppButton>
                        <AppButton onClick={() => setDownloadCompanion(false)}>{t(TK.close)}</AppButton>
                    </ModalFooter>
                </Modal> 
            }
            <Modal isOpen={deleteModal} toggle={() => { setDeleteModal(false); setMigrateToClientOption(undefined); }}>
                <ModalHeader toggle={() => { setDeleteModal(false); setMigrateToClientOption(undefined); }}>{t(TK.areYouSure)}</ModalHeader>
                <ModalBody>
                    <p>{t(TK.youRAboutToDelete)}</p>
                    <AutocompleteTextField
                        id={'client-migrate-to'}
                        label={t(TK.migrateDataToClient)}
                        helpText={t(TK.migrateDataToClientDescription)}
                        value={migrateToClientOption}
                        options={migrationClientSearchConfig.options}
                        optionLabelResolver={migrationClientSearchConfig.optionLabelResolver}
                        searchTextResolver={migrationClientSearchConfig.searchTextResolver}
                        searchMatcher={migrationClientSearchConfig.searchMatcher}
                        onValueChanged={setMigrateToClientOption}
                    />
                </ModalBody>
                <ModalFooter>
                    <AppButton id={'client-confirm-delete-button'} color="danger" onClick={() => {
                        const migrateToClient = migrationClientSearchConfig.getClientByOption(migrateToClientOption);
                        onDelete && onDelete(migrateToClient?.id);
                        setDeleteModal(false);
                        setMigrateToClientOption(undefined);
                    }}>{t(TK.delete)}</AppButton>{' '}
                    <AppButton id={'client-abort-delete-button'} color="secondary" onClick={() => { setDeleteModal(false); setMigrateToClientOption(undefined); }}>{t(TK.cancel)}</AppButton>
                </ModalFooter>
            </Modal>
        </Form>
    );
}

export default ClientForm;
